import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import type { WorkOrderPriceActValue, WorkOrderPriceItem } from 'gql/graphql';
import {
    ActKc2StatusEnum,
    ContractStatusEnum,
    DeviationEnum,
    WorkOrderPriceTypeEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { getNumberFormatter } from 'pages/Project/Common/utils';
import { formatNumberToRu } from 'utils';
import { DeviatedCell } from 'utils/tableCells';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback } from 'react';

import { WorkOrderOtherKC2CostEdit } from './WorkOrderOtherKC2CostEdit';

export enum ActCostColType {
    planCost = 'planCost',
    acceptedCost = 'acceptedCost',
}
interface IActVolumeRenderer extends ICellRendererParams<WorkOrderPriceItem> {
    actId: number;
    seasonId: number;
    decimal: number;
    workOrderStatus: WorkOrderStatusEnum;
    contractStatus: ContractStatusEnum;
    valueKey: keyof WorkOrderPriceActValue;
    deviationKey: keyof WorkOrderPriceActValue;
    isDisabledByProjectStatus: boolean;
    colType: ActCostColType;
    useAgCellRowDataRefresh?: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams?: any;
    skzsPanelRefresh: () => void;
    refreshActs: () => void;
}

export const ActCostRenderer = (props: IActVolumeRenderer) => {
    const {
        data,
        seasonId,
        workOrderStatus,
        actId,
        valueKey,
        deviationKey,
        contractStatus,
        isDisabledByProjectStatus,
        decimal = 2,
        colType,
        api,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        skzsPanelRefresh,
        refreshActs,
    } = props;

    const { reloadHandler } =
        useAgCellRowDataRefresh?.({
            api,
            requestParams: {
                ...useAgCellRowDataRefreshParams,
                ...(data?.type === WorkOrderPriceTypeEnum.Other ? { isOtherItem: true } : { isSMRItem: true }),
            },
        }) || {};

    const onChange = useCallback(
        (reloadRowsIds?: number[] | null) => {
            reloadHandler?.(reloadRowsIds);
            skzsPanelRefresh();
            refreshActs?.();
        },
        [refreshActs, reloadHandler, skzsPanelRefresh],
    );

    if (!data || data?.type === WorkOrderPriceTypeEnum.OtherGroup || data?.type === WorkOrderPriceTypeEnum.RootObject) {
        return '';
    }

    const { itemId, type, isExcludedPrice } = data ?? {};

    const isStatusWorkOrderDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isStatusWorkOrderCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

    const currentSeasonData = data?.seasonValues?.find((season) => season.seasonId === seasonId);
    const currentAct = currentSeasonData?.actValues?.find((act) => act.actId === actId);
    const isEditableCell = currentAct?.actHasEditableCost;
    const value = formatNumberToRu(currentAct?.[valueKey] ?? '', decimal);
    const deviationValue = currentAct?.[deviationKey];

    const isDisabled = Boolean(
        isDisabledByProjectStatus ||
            isStatusWorkOrderDelayed ||
            isStatusWorkOrderCompleted ||
            contractStatus !== ContractStatusEnum.InImplementation ||
            isExcludedPrice,
    );

    if (type === WorkOrderPriceTypeEnum.Other && colType === ActCostColType.planCost) {
        if (!isEditableCell || currentAct?.actStatus !== ActKc2StatusEnum.InProgress) {
            if (deviationValue === DeviationEnum.Up) {
                return <DeviatedCell>{value}</DeviatedCell>;
            }

            return value;
        }

        return (
            <WorkOrderOtherKC2CostEdit
                actKCId={actId}
                workOrderOtherId={itemId}
                defaultValue={value}
                reloadDataHandler={onChange}
                isDisabled={isDisabled}
                decimal={decimal}
                valueType="plan"
                deviation={deviationValue}
            />
        );
    }

    if (type === WorkOrderPriceTypeEnum.Other && colType === ActCostColType.acceptedCost) {
        if (!isEditableCell || currentAct?.actStatus !== ActKc2StatusEnum.OnCheck) {
            if (deviationValue === DeviationEnum.Up) {
                return <DeviatedCell>{value}</DeviatedCell>;
            }

            return value;
        }

        return (
            <WorkOrderOtherKC2CostEdit
                actKCId={actId}
                workOrderOtherId={itemId}
                defaultValue={value}
                reloadDataHandler={onChange}
                isDisabled={isDisabled}
                decimal={decimal}
                valueType="accepted"
                deviation={deviationValue}
            />
        );
    }

    return getNumberFormatter(decimal)({ value }) ?? '';
};

ActCostRenderer.displayName = 'ActVolumeRenderer';
